import React from 'react';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import {
  fetchContentPage,
  fetchDestinations
} from '../integrations/contentful/queries';
import {
  CmsContentPage,
  CmsDestination
} from '../integrations/contentful/types';
import Container from '../components/grid/Container';
import DestinationCard from '../components/destinations/DestinationCard';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import { navbarLayout } from '../layouts/navbarLayout';
import H from '../components/elements/H';
import Breadcrumbs, { BreadcrumbItem } from '../components/head/Breadcrumbs';
import StickyCol from '../components/grid/StickyCol';
import DisplayOn from '../components/common/DisplayOn';
import { columnTopSpacing } from '../styles/theme';
import LabelledCount from '../components/list-pages/LabelledCount';
import LabelledCountRow from '../components/list-pages/LabelledCountRow';
import ContentBlockList from '../components/content-blocks/ContentBlockList';
import HorizontalLine from '../components/common/HorizontalLine';
import LiftedContentBlock from '../components/content-blocks/LiftedContentBlock';
import HeadingWithCount from '../components/common/HeadingWithCount';
import TimMessage from '../components/tim/TimMessage';
import { TimPage } from './TimPage';

interface DestinationListPageProps {
  destinations: CmsDestination[];
  contentPage?: CmsContentPage;
}

export const destinationListPageBreadcrumbItem: BreadcrumbItem = {
  name: t('Matkakohteet'),
  path: Paths.DestinationList
};

function renderLabelledCounts(
  destinationCount: number,
  countriesCount: number,
  tripsCount: number
) {
  return (
    <LabelledCountRow>
      <LabelledCount
        data-testid="cities-count"
        count={destinationCount}
        label={t('kaupunkia')}
      />
      <LabelledCount
        data-testid="countries-count"
        count={countriesCount}
        label={t('maassa')}
      />
      <LabelledCount
        data-testid="trips-count"
        count={tripsCount}
        label={t('erilaista matkaa')}
      />
    </LabelledCountRow>
  );
}

const DESTINATION_INTRODUCTION_MESSAGE = t(
  'Täältä löydät kaikki matkakohteemme eri puolelle Eurooppaa. 🎉 Lähdetäänkö matkalle? Nyt heti?!'
);

const DestinationListPage: TimPage<DestinationListPageProps> = ({
  destinations,
  contentPage
}) => {
  if (!contentPage) {
    return null;
  }

  const newestDestinations = orderBy(
    destinations,
    'sys.createdAt',
    'desc'
  ).slice(0, 2);

  const countriesCount = uniq(
    destinations.map((destination) => {
      return destination.fields.country;
    })
  ).length;
  const tripsCount = uniq(
    flatten(
      destinations.map((destination) => {
        return destination.fields.trips.map((trip) => trip.sys.id);
      })
    )
  ).length;

  return (
    <div data-cy="destination-list-page">
      <SEO
        title={contentPage.fields.seoTitle || t('Matkakohteet')}
        canonicalPath={generatePath(Paths.SearchResults)}
        description={contentPage.fields.seoDescription || t('Matkakohteet')}
      />
      <Breadcrumbs items={[destinationListPageBreadcrumbItem]} />
      <Spacing bottom={4}>{/* TODO: Filtering controls */}</Spacing>
      <Container>
        <Spacing bottom={10}>
          <Row>
            <Col size={[24, 24, 7]}>
              <StickyCol contentTopOffset={columnTopSpacing.sideColumn}>
                <DisplayOn large>
                  <H styling="h2">
                    <strong>{t('Matkakohteet')}</strong>
                  </H>
                  <Spacing top={2.5}>
                    {renderLabelledCounts(
                      destinations.length,
                      countriesCount,
                      tripsCount
                    )}
                  </Spacing>
                  <Spacing top={5}>
                    <TimMessage>{DESTINATION_INTRODUCTION_MESSAGE}</TimMessage>
                  </Spacing>
                </DisplayOn>
              </StickyCol>
            </Col>
            <Col size={[0, 0, 1]}></Col>
            <Col size={[24, 24, 16]}>
              <Row>
                <Col size={24}>
                  <DisplayOn small medium>
                    {renderLabelledCounts(
                      destinations.length,
                      countriesCount,
                      tripsCount
                    )}
                    <Spacing top={4}>
                      <TimMessage>
                        {DESTINATION_INTRODUCTION_MESSAGE}
                      </TimMessage>
                    </Spacing>
                  </DisplayOn>
                  <Spacing top={columnTopSpacing.contentColumn} bottom={5}>
                    <LiftedContentBlock
                      liftTitle={t('Uusi matkakohde')}
                      contentDataElements={newestDestinations}
                      height={[50, 80]}
                      parallax={['120px', '240px']}
                    />
                    <HeadingWithCount
                      icon={'travel-pin'}
                      iconSize={[2, 3, 3]}
                      text={t('Kaikki kohteet')}
                      renderCount={() => t(' — Aakkosjärjestyksessä')}
                    />
                  </Spacing>
                </Col>
              </Row>
              <Row>
                {destinations.map((destination, index) => {
                  return (
                    <Col
                      data-testid="destination"
                      key={index}
                      size={[12, 6, 6]}
                    >
                      {destination.fields.mainImage && (
                        <DestinationCard
                          country={destination.fields.country}
                          mainImage={destination.fields.mainImage}
                          name={destination.fields.title}
                          slug={destination.fields.slug}
                        />
                      )}
                    </Col>
                  );
                })}
              </Row>
            </Col>
          </Row>
        </Spacing>
      </Container>
      <HorizontalLine expand={false} />
      <Spacing top={[8, 14, 14]} bottom={14}>
        {contentPage.fields.blocks && (
          <ContentBlockList blocks={contentPage.fields.blocks} />
        )}
      </Spacing>
    </div>
  );
};

DestinationListPage.getLayout = (page) =>
  navbarLayout(
    <DisplayOn small medium>
      <H styling="h4" tag="div">
        <strong>{t('Matkakohteet')}</strong>
      </H>
    </DisplayOn>,
    page
  );

DestinationListPage.getInitialProps = async function () {
  const [contentPage, destinations] = await Promise.all([
    fetchContentPage('matkakohteet'),
    fetchDestinations()
  ]);
  const httpErrorCode = !contentPage ? 404 : undefined;
  return { contentPage, destinations, httpErrorCode };
};

export default DestinationListPage;
